import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { useOutsideClick } from '../../utils/hooks'
import { AnimatePresence, motion } from 'framer-motion'
import uniqueId from 'lodash/uniqueId'
import { AiOutlineInbox } from 'react-icons/ai'
import { Components } from 'safe'
import Tooltip from '../Display/Tooltip'

const Select: React.FC<SelectProps> = ({
  label = '',
  register,
  placeholder = '',
  options,
  value,
  error,
  rules,
  name,
  size = 'default',
  onChange,
  defaultValue,
  containerClasses,
  setSelectedHelper,
  innerProps,
  disabled = false,
  isChangeNeeded = false,
  emptyText = 'No hay opciones',
}) => {
  const [selected, setSelected] = useState<{
    value: number | string
    text: string
  } | null>(null)
  const [collapse, setCollapse] = useState<boolean>(false)
  const wrapperRef = useRef(null)
  useOutsideClick(wrapperRef, () => setCollapse(false))
  const randomId = uniqueId('label-')

  const setCustomSelected = (value) => {
    setSelected(value)
    setSelectedHelper && setSelectedHelper(value)
  }

  const callbackChange = useCallback(() => onChange && onChange(selected), [
    selected,
  ])

  useEffect(() => {
    if (callbackChange) {
      callbackChange()
    }
  }, [callbackChange])

  useEffect(() => {
    if (defaultValue) {
      setCustomSelected(defaultValue)
    }
  }, [])

  useEffect(() => {
    if (value) setCustomSelected(value)
  }, [value])

  const renderTooltip = (o, k) => {

    if (!o.tooltipValue) return (
      <div
          key={k}
          className={`${
            o.value === selected?.value
              ? 'bg-primary-lighter dark:bg-indigo-900 font-medium'
              : `${!o?.disabled && 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
          } text-sm w-full overflow-ellipsis whitespace-nowrap overflow-x-hidden py-2 px-4`}
          onClick={(e) => {
            if (!o?.disabled) {
              e?.stopPropagation()
              setCollapse(false)
              setCustomSelected(o)
            }
          }}
          style={{ opacity: o?.disabled ? '0.5' : '1' }}
        >
          {o.text}
        </div>
    )

    return (
      <Tooltip
        // key={0}
        title={o?.tooltipValue}
        position="top"
        xOffset={0}
        yOffset={0}
        show={true}
        classes="flex-grow"
      >
        <div
          key={k}
          className={`${
            o.value === selected?.value
              ? 'bg-primary-lighter dark:bg-indigo-900 font-medium'
              : `${!o?.disabled && 'hover:bg-gray-100 dark:hover:bg-gray-700'}`
          } text-sm w-full overflow-ellipsis whitespace-nowrap overflow-x-hidden py-2 px-4`}
          onClick={(e) => {
            if (!o?.disabled) {
              e?.stopPropagation()
              setCollapse(false)
              setCustomSelected(o)
            }
          }}
          style={{ opacity: o?.disabled ? '0.5' : '1' }}
        >
          {o.text}
        </div>
      </Tooltip>
    )
    //overflow-y-auto
  }

  return (
    <div
      ref={wrapperRef}
      onClick={() => !disabled && setCollapse(true)}
      className={`mt-1 cursor-pointer flex flex-col items relative ${containerClasses}`}
    >
      <label
        htmlFor={randomId}
        className="font-medium text-primary-900 text-xs mb-1 dark:text-white"
      >
        {label}
      </label>
      <div className={`flex flex-col-reverse w-full mb-2 relative`}>
        <input
          readOnly
          value={selected?.text || ''}
          className={`cursor-pointer transition overflow-ellipsis overflow-x-hidden whitespace-nowrap w-full dark:bg-gray-600 dark:border-gray-600 dark:text-white
        ${
          size === 'small'
            ? 'h-inputSmall'
            : size === 'large'
            ? 'h-inputLarge'
            : 'h-inputDefault'
        } ${error && 'border-danger focus:border-danger'}`}
          id={randomId}
          placeholder={placeholder}
          type="text"
          {...innerProps}
        />
        <input
          readOnly
          value={selected?.value || ''}
          className="hidden"
          ref={register ? register(rules) : undefined}
          name={name}
        />
        <label
          htmlFor={name}
          className="absolute cursor-pointer right-1 top-1/2 -mt-2"
        >
          {<BiChevronDown />}
        </label>
      </div>
      <AnimatePresence>
        {collapse && (
          <motion.div
            className={`w-full py-1 rounded shadow-md max-h-80 absolute bg-white dark:bg-gray-800 z-[9999] top-full border-gray-100 border-solid border-1 ${options.some(x => x.tooltipValue) ? 'overflow-visible' : 'overflow-y-auto'}`}
            initial="closed"
            animate="open"
            exit="closed"
            transition={{ duration: 0.2, ease: [0.04, 0.62, 0.23, 0.98] }}
            variants={{
              open: { opacity: 1, height: 'max-content' },
              closed: { opacity: 0, height: 60 },
            }}
          >
            {options.length > 0 ? (
              options.map((o, k) => <>{renderTooltip(o, k)}</>)
            ) : (
              <div className="pt-4 text-gray-300 flex justify-center flex-col items-center bg-transparent text-center">
                <AiOutlineInbox size="1.6rem" />
                <p className="text-sm">{emptyText}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Select

type InnerProps = JSX.IntrinsicElements['input']

interface SelectProps {
  label?: string
  size?: 'small' | 'large' | 'default'
  placeholder?: string
  onChange?: Function
  defaultValue?: Components.SelectOption
  options: Components.SelectOption[]
  value?: Components.SelectOption
  innerProps?: InnerProps
  containerClasses?: string
  disabled?: boolean
  // selectedHelper?: any
  setSelectedHelper?: Function
  [key: string]: any
  emptyText?: string
}
